import { useEffect, useState } from 'react';
import { NavLink, Outlet, Navigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { apiFetch, ApiError, Me, StaffRole } from '../api/client';
import PortalLoading from '../shell/PortalLoading';

export interface AdminCtx { staffRole: StaffRole; }

export default function AdminShell() {
  const { staffAccount, signOut, getToken } = useAuth();
  const username = staffAccount?.username ?? '';
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<Me>('/portal/me', await getToken());
        setMe(data);
      } catch (e) {
        // 401 is being handled by the global onUnauthorized (redirect to
        // sign-in). Don't paint an error banner over the top of that.
        if (e instanceof ApiError && e.status === 401) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [getToken]);

  if (error) return (
    <div style={{ padding: 32 }}>
      <h2 style={{ color: 'var(--mi-navy)' }}>Something went wrong</h2>
      <p style={{ color: '#666' }}>{error}</p>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn-primary" onClick={() => window.location.reload()}>Retry</button>
        <button onClick={signOut} style={{ background: 'transparent', border: '1px solid var(--mi-border)', borderRadius: 6, padding: '6px 12px' }}>Sign out</button>
      </div>
    </div>
  );
  if (!me) return <PortalLoading />;

  const staffRole = me.user.staffRole ?? null;
  if (!staffRole) {
    // No role assigned — bounce to the client landing (external flow) or show a friendly block.
    return (
      <div style={{ padding: 32 }}>
        <h2>No access</h2>
        <p style={{ color: '#555' }}>
          Your Mount Insights account hasn&rsquo;t been granted admin access yet. Contact an administrator to request a role.
        </p>
        <button onClick={signOut} style={{ padding: '6px 12px' }}>Sign out</button>
      </div>
    );
  }

  const isAdmin = staffRole === 'admin';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', minHeight: '100vh' }}>
      <aside style={{ background: '#2b2f33', color: 'white', padding: '1rem', position: 'relative' }}>
        <img src="/MI-Logo-DarkBackground.png" alt="Mount Insights" style={{ width: '100%', maxWidth: 120, height: 'auto', marginBottom: 20, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
        <div style={{ fontSize: 12, color: '#bfc6cc', textTransform: 'uppercase' }}>
          {isAdmin ? 'Admin' : 'Client Admin'}
        </div>
        <nav style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <NavLink to="/admin/clients" style={navLinkStyle}>Clients</NavLink>
          {isAdmin && <NavLink to="/admin/users" style={navLinkStyle}>Users</NavLink>}
          {isAdmin && <NavLink to="/admin/modules" style={navLinkStyle}>Modules</NavLink>}
          {isAdmin && <NavLink to="/admin/reports" style={navLinkStyle}>Reports</NavLink>}
          {isAdmin && <NavLink to="/admin/invites" style={navLinkStyle}>Invites</NavLink>}
          {isAdmin && <NavLink to="/admin/audit" style={navLinkStyle}>Login Audit</NavLink>}
        </nav>
        <div style={{ position: 'absolute', bottom: 16, fontSize: 12 }}>
          <div>{username}</div>
          <button onClick={signOut} style={{ marginTop: 8, background: 'transparent', color: 'white', border: '1px solid #bfc6cc' }}>
            Sign out
          </button>
        </div>
      </aside>
      <main style={{ padding: '2rem' }}>
        <Outlet context={{ staffRole } satisfies AdminCtx} />
      </main>
    </div>
  );
}

export function useAdminCtx(): AdminCtx {
  return useOutletContext<AdminCtx>();
}

export function AdminOnly({ children }: { children: React.ReactNode }) {
  const { staffRole } = useAdminCtx();
  if (staffRole !== 'admin') return <Navigate to="/admin/clients" replace />;
  return <>{children}</>;
}

const navLinkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
  color: isActive ? 'var(--mi-orange)' : 'white',
  padding: '6px 8px',
  borderRadius: 6,
  background: isActive ? 'rgba(255,255,255,0.06)' : 'transparent',
  textDecoration: 'none',
});
